import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useTheme } from "../Utils/useTheme";
import { useSEO } from "../Utils/useSEO";
import SupplyChainNetwork3D from "../components/Three/SupplyChainNetwork3D";
import ContactGlobe3D from "../components/Three/ContactGlobe3D";
import {
  ArrowRight,
  Sparkles,
  MapPin,
  Phone,
  Mail,
  Clock,
  ChevronDown,
  ChevronUp,
  Users,
  ShieldCheck,
  Truck,
  Layers,
  HelpCircle,
  Building,
  DollarSign,
  Briefcase,
  Compass,
  Send,
  Calendar,
  UserCheck,
  CheckCircle2,
  Headphones,
  Award
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import { apiService } from "../Services/apiService";
import { toast } from "react-toastify";

export default function ContactPage() {
  useSEO({
    title: "Contact Sales & Support // Schedule a Live Demo",
    description: "Connect with procurement consultants, schedule Platform interactive walkthroughs, or talk to our sales desk."
  });

  const { theme } = useTheme();
  const isDark = theme !== "light";

  // Journey Selector state
  const [activeJourney, setActiveJourney] = useState("buyer");

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    industry: "",
    size: "10-50",
    reason: "Request Demo",
    message: ""
  });

  // Booking states
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("10:00 AM");
  const [bookingInterest, setBookingInterest] = useState("Full Platform Demo");
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Office Locations state
  const [activeLocationIndex, setActiveLocationIndex] = useState(0);

  // FAQ state
  const [activeFaq, setActiveFaq] = useState(null);



  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiService.submitContactForm(formData);
      setIsSubmitting(false);
      setFormSubmitted(true);
      toast.success("Operational inquiry successfully dispatched!");
    } catch (error) {
      setIsSubmitting(false);
      toast.error(error.message || "Failed to submit operational inquiry.");
      // Fallback for visual testing when no API server is connected
      setFormSubmitted(true);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingDate) return;
    try {
      await apiService.submitBooking({ bookingDate, bookingTime, bookingInterest });
      setBookingSuccess(true);
      toast.success("Live reservation scheduled successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to schedule live reservation.");
      // Fallback for visual testing when no API server is connected
      setBookingSuccess(true);
    }
  };



  const journeys = [
    {
      id: "buyer",
      icon: <Building className="w-5 h-5 text-brand-orange" />,
      title: "Buyer / Procurement Team",
      desc: "Automate sourcing, RFQs, purchase orders, supplier onboarding, and procurement workflows.",
      cta: "Explore Procurement Solutions",
      color: "border-brand-orange/30 hover:border-brand-orange/60"
    },
    {
      id: "supplier",
      icon: <Users className="w-5 h-5 text-brand-amber" />,
      title: "Supplier / Vendor",
      desc: "Manage orders, deliveries, invoices, contracts, and collaboration through Supply Spot.",
      cta: "Explore Supplier Portal",
      color: "border-brand-amber/30 hover:border-brand-amber/60"
    },
    {
      id: "logistics",
      icon: <Truck className="w-5 h-5 text-brand-gold" />,
      title: "Logistics Partner",
      desc: "Leverage Smart Delivery, GPS tracking, dock scheduling, and JIT logistics.",
      cta: "Explore Logistics Solutions",
      color: "border-brand-gold/30 hover:border-brand-gold/60"
    },
    {
      id: "finance",
      icon: <DollarSign className="w-5 h-5 text-brand-orange" />,
      title: "Finance / Accounts Payable",
      desc: "Automate invoice processing, AI matching, approvals, and supplier payments.",
      cta: "Explore AP Automation",
      color: "border-brand-orange/30 hover:border-brand-orange/60"
    }
  ];

  const contactOptions = [
    {
      title: "Sales Enquiry",
      icon: <Briefcase className="w-6 h-6 text-brand-orange" />,
      desc: "Talk to our product specialists.",
      cta: "Contact Sales",
      action: () => {
        const el = document.getElementById("contact-form-section");
        el?.scrollIntoView({ behavior: "smooth" });
        setFormData(prev => ({ ...prev, reason: "Sales Discussion" }));
      }
    },
    {
      title: "Request a Demo",
      icon: <Layers className="w-6 h-6 text-brand-amber" />,
      desc: "Experience Supply Spot in action.",
      cta: "Book Demo",
      action: () => {
        const el = document.getElementById("booking-section");
        el?.scrollIntoView({ behavior: "smooth" });
      }
    },
    {
      title: "Technical Support",
      icon: <Headphones className="w-6 h-6 text-brand-gold" />,
      desc: "Get technical assistance.",
      cta: "Open Support Ticket",
      action: () => {
        const el = document.getElementById("contact-form-section");
        el?.scrollIntoView({ behavior: "smooth" });
        setFormData(prev => ({ ...prev, reason: "Technical Support" }));
      }
    },
    {
      title: "Partnership Program",
      icon: <Award className="w-6 h-6 text-brand-orange" />,
      desc: "Become a partner.",
      cta: "Contact Partnership Team",
      action: () => {
        const el = document.getElementById("contact-form-section");
        el?.scrollIntoView({ behavior: "smooth" });
        setFormData(prev => ({ ...prev, reason: "Partnership" }));
      }
    }
  ];

  const faqData = [
    {
      q: "How long does implementation take?",
      a: "Our standard implementation timeline ranges from 2 days for Starter setups to 4-6 weeks for custom global enterprise configurations including legacy database integrations."
    },
    {
      q: "Do you integrate with SAP?",
      a: "Yes. Supply Spot provides native, bi-directional, real-time sync with SAP (S/4HANA and ECC), Oracle, Microsoft Dynamics 365, Swift, and customized corporate databases."
    },
    {
      q: "Can I request a customized demo?",
      a: "Absolutely! Simply select your industry focus and product modules in our meeting booking widget, and our specialists will configure a staging environment tailored to your workflows."
    },
    {
      q: "Do you support multi-company operations?",
      a: "Yes. Our Enterprise plan provides robust multi-company partitioning, legal entity segmentation, multi-country localization, and centralized corporate governance."
    },
    {
      q: "Do you offer implementation services?",
      a: "Yes. We offer fully-managed migration and setup services including onboarding workshops, custom mapping for custom ledgers, and dedicated solutions engineering support."
    },
    {
      q: "Can Supply Spot be customized?",
      a: "Yes, we support highly customizable automated RFQ rules, invoice discrepancy tolerances, KPI scoring logic, custom document templates, and API integrations."
    }
  ];

  const officeLocations = [
    {
      city: "Coimbatore",
      address: "N8, Souvenir, Kalapatti Road, Coimbatore - 641014, Tamil Nadu, India",
      phone: "+91 96 5556 0021",
      hours: "9:00 AM – 6:00 PM (IST)",
      team: "Regional Logistics Hub Solutions Engineers"
    },
    {
      city: "Bangalore",
      address: "Prestige Tech Park, Outer Ring Rd, Marathahalli, Bangalore, KA 560103",
      phone: "+91 96 5556 0021",
      hours: "9:00 AM – 6:00 PM (IST)",
      team: "Procurement & AP Core Development Teams"
    },
    {
      city: "Dubai",
      address: "DIFC Gate Precinct, Building 4, Level 5, Dubai, UAE",
      phone: "+971 1 234 5678",
      hours: "9:00 AM – 6:00 PM (GST)",
      team: "Middle East Logistics Partnerships & Enterprise Sales"
    },
    {
      city: "Singapore",
      address: "Marina One West Tower, 9 Straits View, Singapore 018937",
      phone: "+65 1234 5678",
      hours: "9:00 AM – 6:00 PM (SGT)",
      team: "Asia Pacific Enterprise Strategy & Customer Success"
    }
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Glow Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[140px] -z-10 pointer-events-none" />

      {/* HERO SECTION */}
      <section className="relative pt-36 pb-20 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
        {/* Left Hero content */}
        <div className="flex-1 text-left relative z-10 flex flex-col gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider self-start shadow-md"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>GLOBAL SUPPLY CHAIN CONTROL</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-text-primary leading-none font-title"
          >
            Let's Build a <br />
            <span className="bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-md">
              Smarter Supply Chain
            </span> <br />
            Together
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-text-secondary text-sm md:text-base max-w-xl leading-relaxed"
          >
            Connect with our experts to transform supplier management, procurement, logistics, and accounts payable operations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap items-center gap-4 mt-2"
          >
            <button
              onClick={() => {
                const el = document.getElementById("booking-section");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-6 py-3.5 font-bold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              BOOK A DEMO
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("contact-form-section");
                el?.scrollIntoView({ behavior: "smooth" });
                setFormData(prev => ({ ...prev, reason: "Sales Discussion" }));
              }}
              className={`px-6 py-3.5 font-bold text-xs tracking-wider rounded-lg bg-transparent border transition-all duration-300 ${isDark
                  ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                  : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                }`}
            >
              TALK TO SALES
            </button>
          </motion.div>
        </div>

        {/* Right Hero Interactive 3D Supply Chain Graphic */}
        <ScrollReveal delay={0.2} className="flex-1 w-full h-[400px]">
          <div className="w-full h-full glass-panel rounded-3xl relative overflow-hidden shadow-2xl">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-2.5 py-1 bg-bg-primary/80 border border-brand-orange/20 rounded-full">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-orange animate-pulse" />
              <span className="text-[9px] font-title font-bold text-text-primary tracking-wider uppercase">
                NETWORK SIMULATION ACTIVE
              </span>
            </div>
            <SupplyChainNetwork3D />
          </div>
        </ScrollReveal>
      </section>

      {/* CHOOSE YOUR JOURNEY */}
      <section className="px-6 py-24 max-w-7xl mx-auto relative z-10 text-center">
        <ScrollReveal className="max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
            <Compass className="w-3.5 h-3.5" />
            <span>ROLE PERSONALIZATION</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-text-primary font-title">
            How Can We Help You Today?
          </h2>
          <p className="text-text-secondary text-sm">
            Choose your role and receive a personalized experience.
          </p>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.08} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {journeys.map((journey) => (
            <ScrollRevealItem key={journey.id} className="flex">
              <div
                onClick={() => setActiveJourney(journey.id)}
                className={`w-full rounded-2xl p-6 border text-left cursor-pointer transition-all duration-300 flex flex-col justify-between ${activeJourney === journey.id
                    ? "border-brand-orange bg-bg-secondary/80 dark:bg-white/[0.08] shadow-glow-orange scale-105"
                    : `bg-bg-primary/40 dark:bg-white/[0.05] border-brand-orange/20 hover:border-brand-amber/40 hover:-translate-y-1`
                  }`}
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center mb-4">
                    {journey.icon}
                  </div>
                  <h3 className="font-bold text-sm text-text-primary mb-2">{journey.title}</h3>
                  <p className="text-text-secondary text-xs leading-relaxed">{journey.desc}</p>
                </div>

                <div className="mt-6">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const el = document.getElementById("booking-section");
                      el?.scrollIntoView({ behavior: "smooth" });
                      setBookingInterest(journey.title.split(" / ")[0] + " Demo");
                    }}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-brand-orange hover:text-brand-amber transition-colors`}
                  >
                    <span>{journey.cta}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollReveal>
      </section>

      {/* CONTACT OPTIONS */}
      <section className="px-6 py-12 max-w-7xl mx-auto relative z-10">
        <ScrollReveal staggerChildren={0.06} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactOptions.map((opt, idx) => (
            <ScrollRevealItem key={idx} className="flex">
              <div
                className="w-full glass-panel rounded-2xl p-6 text-center flex flex-col items-center gap-4 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center">
                  {opt.icon}
                </div>
                <h3 className="font-bold text-sm text-text-primary">{opt.title}</h3>
                <p className="text-text-secondary text-xs">{opt.desc}</p>
                <button
                  onClick={opt.action}
                  className="w-full py-2.5 px-4 bg-transparent hover:bg-brand-orange/5 text-brand-orange border border-brand-orange/40 hover:border-brand-amber hover:text-brand-amber rounded-xl text-xs font-bold transition-all"
                >
                  {opt.cta}
                </button>
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollReveal>
      </section>

      {/* INTERACTIVE CONTACT FORM SECTION */}
      <section id="contact-form-section" className="px-6 py-20 max-w-7xl mx-auto relative z-10 border-y border-border-theme">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">

          {/* Left Side: HQ Dashboard */}
          <ScrollReveal className="lg:col-span-5 flex" y={30}>
            <div className="w-full glass-panel p-8 rounded-3xl flex flex-col justify-between relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-48 h-48 bg-brand-orange/10 rounded-full blur-3xl -z-10 pointer-events-none" />

              <div>
                <h3 className="font-bold text-lg mb-6 text-text-primary">HQ Status Dashboard</h3>

                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-4 border-b border-border-theme pb-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange">
                      <Compass className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">Operational Status</div>
                      <div className="text-sm font-bold text-slate-800 dark:text-slate-100">Live Telemetry Pipeline Active</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 border-b border-border-theme pb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-brand-amber">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">Aether AI System</div>
                      <div className="text-sm font-bold text-slate-800 dark:text-slate-100">Running Diagnostics</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">Escrow Released Rate</div>
                      <div className="text-sm font-bold text-emerald-500">99.85% Automatic releases</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-4 bg-brand-orange/5 border border-brand-orange/20 rounded-2xl flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-brand-orange animate-ping" />
                <p className="text-[10px] text-text-secondary leading-relaxed">
                  Connect with Dubai, Singapore, Bangalore, or Coimbatore teams for local setup support.
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Right Side: Form */}
          <ScrollReveal delay={0.1} className="lg:col-span-7 flex" y={30}>
            <div className="w-full glass-panel p-8 rounded-3xl shadow-2xl relative">
              <h3 className="font-bold text-lg text-text-primary mb-6 uppercase tracking-wider font-title">Send Operational Inquiry</h3>

            <AnimatePresence mode="wait">
              {!formSubmitted ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleFormSubmit}
                  className="flex flex-col gap-5 text-sm"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Full Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Company Name</label>
                      <input
                        type="text"
                        required
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="Logistics Corp"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Business Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="john@company.com"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Phone Number</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Country</label>
                      <input
                        type="text"
                        required
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="India"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Industry</label>
                      <input
                        type="text"
                        required
                        value={formData.industry}
                        onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                        placeholder="Manufacturing"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-text-secondary font-semibold">Company Size</label>
                      <select
                        value={formData.size}
                        onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                        className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                      >
                        <option value="1-10">1-10 Employees</option>
                        <option value="10-50">10-50 Employees</option>
                        <option value="50-250">50-250 Employees</option>
                        <option value="250-1000">250-1000 Employees</option>
                        <option value="1000+">1000+ Employees</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Reason for Contact</label>
                    <select
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none w-full"
                    >
                      <option value="Request Demo">Request Demo</option>
                      <option value="Product Enquiry">Product Enquiry</option>
                      <option value="Sales Discussion">Sales Discussion</option>
                      <option value="Implementation Services">Implementation Services</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Partnership">Partnership</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Message</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none resize-none"
                      placeholder="Outline your logistics or compliance needs..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-brand-orange hover:bg-brand-amber hover:shadow-brand-orange/20 hover:shadow-lg text-white font-bold text-center text-xs tracking-wider rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-md shadow-brand-orange/15"
                  >
                    {isSubmitting ? (
                      <span>DISPATCHING REQUEST...</span>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>SEND REQUEST</span>
                      </>
                    )}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  {/* Truck Delivery Animation */}
                  <div className="w-full max-w-sm h-28 relative overflow-hidden bg-bg-secondary border border-brand-orange/15 rounded-2xl mb-8 flex items-center">
                    <motion.div
                      initial={{ x: "-100%" }}
                      animate={{ x: "120%" }}
                      transition={{ duration: 2.2, ease: "linear" }}
                      className="text-brand-orange flex items-center gap-2 absolute"
                    >
                      <Truck className="w-10 h-10 fill-brand-orange/10" />
                      <div className="bg-brand-orange text-text-primary text-[8px] font-black px-2 py-0.5 rounded shadow-md whitespace-nowrap">
                        DISPATCHING ENQUIRY
                      </div>
                    </motion.div>

                    <div className="absolute right-4 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center animate-pulse" />
                  </div>

                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500 mb-6">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>

                  <h4 className="text-xl font-bold text-text-primary mb-2">Request Successfully Dispatched</h4>
                  <p className="text-text-secondary text-xs leading-relaxed max-w-sm">
                    A Smart Delivery truck has successfully carried your request to Supply Spot HQ. Our team will review your telemetry criteria and respond within 12 business hours.
                  </p>

                  <button
                    onClick={() => {
                      setFormSubmitted(false);
                      setFormData({
                        name: "",
                        company: "",
                        email: "",
                        phone: "",
                        country: "",
                        industry: "",
                        size: "10-50",
                        reason: "Request Demo",
                        message: ""
                      });
                    }}
                    className="mt-8 px-6 py-2.5 bg-transparent border border-slate-700 hover:border-brand-orange text-text-secondary hover:text-text-primary rounded-xl text-xs font-bold transition-all"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </ScrollReveal>

        </div>
      </section>

      {/* BOOK A LIVE DEMO SECTION */}
      <section id="booking-section" className="px-6 py-20 max-w-5xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
            <Calendar className="w-3.5 h-3.5" />
            <span>DIRECT SCHEDULER</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-text-primary font-title">
            Book a Live Demo
          </h2>
          <p className="text-text-secondary text-sm">
            Experience the Supply Spot interface customized to your target compliance criteria.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className="glass-panel p-8 shadow-2xl relative">
          <AnimatePresence mode="wait">
            {!bookingSuccess ? (
              <motion.form
                key="booking-form"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleBookingSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Left Booking Column */}
                <div className="flex flex-col gap-4">
                  <h3 className="font-bold text-sm text-text-primary border-b border-border-theme pb-2 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-brand-orange" />
                    Available Consultant
                  </h3>

                  <div className="flex items-center gap-4 bg-bg-secondary/60 p-4 border border-text-muted/10 rounded-2xl">
                    <div className="w-12 h-12 rounded-full bg-brand-orange/20 border border-brand-orange/30 flex items-center justify-center text-brand-orange font-black">
                      SK
                    </div>
                    <div>
                      <div className="text-xs font-bold text-text-primary">Sanjay Kulkarni</div>
                      <div className="text-[10px] text-text-muted">Enterprise Solutions Architect</div>
                      <div className="text-[9px] text-emerald-500 font-bold uppercase mt-1">AVAILABLE TODAY</div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 mt-2">
                    <label className="text-xs text-text-secondary font-semibold">Meeting Duration</label>
                    <input
                      type="text"
                      disabled
                      value="30 Minutes Walkthrough"
                      className="px-4 py-2.5 bg-bg-secondary border border-border-theme rounded-lg text-text-secondary focus:outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Time Zone Selection</label>
                    <select className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none">
                      <option>India Standard Time (IST) - GMT+5:30</option>
                      <option>Singapore Time (SGT) - GMT+8:00</option>
                      <option>Gulf Standard Time (GST) - GMT+4:00</option>
                    </select>
                  </div>
                </div>

                {/* Right Booking Column */}
                <div className="flex flex-col gap-4">
                  <h3 className="font-bold text-sm text-text-primary border-b border-border-theme pb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-brand-orange" />
                    Meeting Parameters
                  </h3>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Preferred Date</label>
                    <input
                      type="date"
                      required
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none w-full"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Preferred Time</label>
                    <select
                      value={bookingTime}
                      onChange={(e) => setBookingTime(e.target.value)}
                      className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                    >
                      <option value="10:00 AM">10:00 AM - 10:30 AM</option>
                      <option value="11:30 AM">11:30 AM - 12:00 PM</option>
                      <option value="2:00 PM">2:00 PM - 2:30 PM</option>
                      <option value="3:30 PM">3:30 PM - 4:00 PM</option>
                      <option value="5:00 PM">5:00 PM - 5:30 PM</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-text-secondary font-semibold">Product Interest Focus</label>
                    <select
                      value={bookingInterest}
                      onChange={(e) => setBookingInterest(e.target.value)}
                      className="px-4 py-2.5 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-text-primary focus:outline-none"
                    >
                      <option value="Supplier Management">Supplier Management</option>
                      <option value="Procurement Automation">Procurement Automation</option>
                      <option value="Smart Delivery">Smart Delivery</option>
                      <option value="JIT Logistics">JIT Logistics</option>
                      <option value="Accounts Payable Automation">Accounts Payable Automation</option>
                      <option value="Full Platform Demo">Full Platform Demo</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-brand-orange hover:bg-brand-amber text-white font-bold text-center text-xs tracking-wider rounded-xl transition-all duration-300 shadow-md shadow-brand-orange/15 hover:shadow-brand-orange/30 mt-2"
                  >
                    CONFIRM LIVE RESERVATION
                  </button>
                </div>
              </motion.form>
            ) : (
              <motion.div
                key="booking-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center text-center py-10"
              >
                <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center text-emerald-500 mb-6">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h4 className="text-xl font-bold text-text-primary mb-2">Live Reservation Scheduled</h4>
                <p className="text-text-secondary text-xs leading-relaxed max-w-sm">
                  Your walkthrough of **{bookingInterest}** is booked for **{bookingDate}** at **{bookingTime}** with Sanjay Kulkarni.
                  A calendar invite and custom demo setup credentials have been sent to your email.
                </p>
                <button
                  onClick={() => setBookingSuccess(false)}
                  className="mt-6 px-6 py-2.5 bg-bg-secondary border border-border-theme hover:border-brand-orange text-text-secondary hover:text-text-primary rounded-xl text-xs font-bold transition-all"
                >
                  Adjust Booking Details
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </ScrollReveal>
      </section>

      {/* GLOBAL PRESENCE */}
      <section className="px-6 py-20 bg-slate-50/50 dark:bg-slate-950/20 border-t border-border-theme relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left Details */}
          <ScrollReveal className="lg:col-span-6 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
                <MapPin className="w-3.5 h-3.5" />
                <span>OFFICE INDEX</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-text-primary font-title">
                Our Global Network
              </h2>
              <p className="text-text-secondary text-sm">
                Click a logistics tower to display coordinates, addresses, and local team resources.
              </p>
            </div>

            {/* City buttons */}
            <div className="flex flex-wrap gap-2.5">
              {officeLocations.map((loc, idx) => (
                <button
                  key={loc.city}
                  onClick={() => setActiveLocationIndex(idx)}
                  className={`px-5 py-3 rounded-xl font-bold text-xs tracking-wider border transition-all ${activeLocationIndex === idx
                      ? "bg-brand-orange border-brand-orange text-white shadow-md shadow-orange-500/20"
                      : "bg-bg-secondary border-border-theme text-text-secondary hover:border-brand-orange/30"
                    }`}
                >
                  {loc.city}
                </button>
              ))}
            </div>

            {/* Address Info Cards */}
            <div className="bg-bg-secondary/85 border border-brand-orange/30 p-6 rounded-2xl flex flex-col gap-4 shadow-xl">
              <div>
                <h4 className="text-[10px] text-brand-orange font-bold uppercase tracking-wider">Office Address</h4>
                <p className="text-xs text-text-primary leading-relaxed mt-1">{officeLocations[activeLocationIndex].address}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border-theme pt-4">
                <div>
                  <h4 className="text-[10px] text-brand-orange font-bold uppercase tracking-wider">Phone Number</h4>
                  <a
                    href={`tel:${officeLocations[activeLocationIndex].phone.replace(/\s+/g, "")}`}
                    className="text-xs font-semibold text-text-primary hover:text-brand-orange leading-relaxed mt-1 flex items-center gap-1.5 transition-colors w-fit"
                  >
                    <Phone className="w-3.5 h-3.5 text-slate-500" />
                    {officeLocations[activeLocationIndex].phone}
                  </a>
                </div>
                <div>
                  <h4 className="text-[10px] text-brand-orange font-bold uppercase tracking-wider">Working Hours</h4>
                  <p className="text-xs text-text-primary leading-relaxed mt-1 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-500" />
                    {officeLocations[activeLocationIndex].hours}
                  </p>
                </div>
              </div>

              <div className="border-t border-border-theme pt-4">
                <h4 className="text-[10px] text-brand-orange font-bold uppercase tracking-wider">Local Specialists</h4>
                <p className="text-xs text-text-secondary leading-relaxed mt-1 flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-slate-500" />
                  {officeLocations[activeLocationIndex].team}
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Right Globe View */}
          <ScrollReveal delay={0.15} className="lg:col-span-6 w-full h-[400px]">
            <div className="w-full h-full bg-bg-secondary border border-brand-orange/25 rounded-3xl relative overflow-hidden shadow-2xl flex items-center justify-center">
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-2.5 py-1 bg-bg-primary/80 border border-brand-orange/20 rounded-full">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-ping" />
                <span className="text-[9px] font-title font-bold text-text-primary tracking-wider uppercase">
                  SPHERICAL LOCK ACTIVE
                </span>
              </div>

              <ContactGlobe3D activeLocationIndex={activeLocationIndex} />
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* CONTACT INFO FOOTER SUMMARY */}
      <section className="px-6 py-12 max-w-7xl mx-auto relative z-10">
        <ScrollReveal staggerChildren={0.05} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ScrollRevealItem className="flex">
            <a
              href="mailto:sales@supplyspot.ai"
              className="w-full bg-bg-secondary border border-border-theme p-6 rounded-2xl flex items-center gap-4 hover:border-brand-orange/40 hover:shadow-glow-orange/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange group-hover:text-white transition-all">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Sales Team</h4>
                <p className="text-xs font-bold text-text-primary mt-0.5 group-hover:text-brand-orange transition-colors">sales@supplyspot.ai</p>
              </div>
            </a>
          </ScrollRevealItem>

          <ScrollRevealItem className="flex">
            <a
              href="mailto:support@supplyspot.ai"
              className="w-full bg-bg-secondary border border-border-theme p-6 rounded-2xl flex items-center gap-4 hover:border-brand-orange/40 hover:shadow-glow-orange/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange group-hover:text-white transition-all">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Support Team</h4>
                <p className="text-xs font-bold text-text-primary mt-0.5 group-hover:text-brand-orange transition-colors">support@supplyspot.ai</p>
              </div>
            </a>
          </ScrollRevealItem>

          <ScrollRevealItem className="flex">
            <a
              href="tel:+919655560021"
              className="w-full bg-bg-secondary border border-border-theme p-6 rounded-2xl flex items-center gap-4 hover:border-brand-orange/40 hover:shadow-glow-orange/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange group-hover:text-white transition-all">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Corporate Line</h4>
                <p className="text-xs font-bold text-text-primary mt-0.5 group-hover:text-brand-orange transition-colors">+91 96 5556 0021</p>
              </div>
            </a>
          </ScrollRevealItem>

          <ScrollRevealItem className="flex">
            <div className="w-full bg-bg-secondary border border-border-theme p-6 rounded-2xl flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Business Hours</h4>
                <p className="text-xs font-bold text-text-primary mt-0.5">Monday – Friday</p>
              </div>
            </div>
          </ScrollRevealItem>
        </ScrollReveal>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS SECTION */}
      <section className="px-6 py-20 bg-slate-50/50 dark:bg-slate-950/20 border-t border-border-theme relative z-10">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>FAQ INDEX</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-text-primary font-title">
              Frequently Asked Questions
            </h2>
            <p className="text-text-secondary text-sm">
              Answers to standard onboarding, ERP integration, and system setup support.
            </p>
          </ScrollReveal>

          <ScrollReveal staggerChildren={0.06} className="flex flex-col gap-4">
            {faqData.map((faq, idx) => (
              <ScrollRevealItem key={idx}>
                <div className="bg-bg-secondary border border-border-theme rounded-2xl overflow-hidden transition-all shadow-sm">
                  <button
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                    className="w-full p-6 text-left font-bold text-text-primary text-sm flex items-center justify-between gap-4 hover:text-brand-orange transition-colors"
                  >
                    <span>{faq.q}</span>
                    {activeFaq === idx ? (
                      <ChevronUp className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {activeFaq === idx && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <div className="px-6 pb-6 text-xs text-text-secondary leading-relaxed border-t border-border-theme pt-4 bg-bg-primary/50">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </ScrollRevealItem>
            ))}
          </ScrollReveal>
        </div>
      </section>



      {/* FINAL CTA SECTION */}
      <section className="py-24 relative overflow-hidden bg-bg-secondary dark:bg-slate-950 text-text-primary z-10 border-t border-border-theme">
        <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/10 via-transparent to-amber-500/10 pointer-events-none opacity-60" />
        <div className="absolute inset-0 bg-grid-pattern-40 pointer-events-none" />

        <ScrollReveal className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col gap-6 items-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide leading-tight font-title">
            Ready to Transform Your Supply Chain?
          </h2>
          <p className="text-text-secondary text-sm md:text-base leading-relaxed max-w-xl">
            Join organizations using Supply Spot to streamline supplier management, procurement, logistics, and payments.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-4 mt-6">
            <button
              onClick={() => {
                const el = document.getElementById("booking-section");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-8 py-4 font-bold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-orange/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              BOOK DEMO
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("contact-form-section");
                el?.scrollIntoView({ behavior: "smooth" });
                setFormData(prev => ({ ...prev, reason: "Sales Discussion" }));
              }}
              className={`px-8 py-4 font-bold text-xs tracking-wider rounded-lg bg-transparent border transition-all duration-300 ${isDark
                  ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                  : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                }`}
            >
              TALK TO SALES
            </button>
          </div>
        </ScrollReveal>
      </section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
